import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, RefreshCw, Search, Save } from 'lucide-react'
import {
  WeeklyCheckInRow,
  fetchAllWeeklyCheckIns,
  fetchAllPreferences,
  saveCheckInFeedback,
} from '../../lib/instantData'
import { isAdminEmail } from '../../lib/adminEmails'
import { db } from '../../lib/db'
import AuthedImage from '../../components/ui/AuthedImage'
import GlassCard from '../../components/ui/GlassCard'
import GradientButton from '../../components/ui/GradientButton'
import PanelShell from '../../components/ui/PanelShell'

interface ClientGroup {
  userId: string
  checkIns: WeeklyCheckInRow[]
  latest: WeeklyCheckInRow
  pendingCount: number
}

interface ClientInfo {
  fullName: string
  email: string
}

const formatDate = (isoDate: string) => {
  const d = new Date(`${isoDate}T00:00:00`)
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
}

const AdminProgressCheckIns: React.FC = () => {
  const navigate = useNavigate()
  const { user: instantUser } = db.useAuth()
  const token = instantUser?.refresh_token

  const [clients, setClients] = useState<ClientGroup[]>([])
  const [clientInfo, setClientInfo] = useState<Record<string, ClientInfo>>({})
  const [loading, setLoading] = useState(true)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [selectedCheckInId, setSelectedCheckInId] = useState<string | null>(null)
  const [feedbackDraft, setFeedbackDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const load = useCallback(async () => {
    if (!token) return
    try {
      const [clientRows, preferences] = await Promise.all([
        fetchAllWeeklyCheckIns(token),
        fetchAllPreferences(),
      ])
      setClients(clientRows)

      const info: Record<string, ClientInfo> = {}
      preferences
        .filter((p) => !isAdminEmail(p.email))
        .forEach((p) => {
          info[p.user_id] = { fullName: p.full_name || 'Unknown Client', email: p.email }
        })
      setClientInfo(info)
    } catch (err) {
      console.error('Failed to load check-ins:', err)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    load()
  }, [load])

  const selectedClient = clients.find((c) => c.userId === selectedUserId)
  const selectedCheckIn = selectedClient?.checkIns.find((c) => c.id === selectedCheckInId)

  const openClient = (userId: string) => {
    setSelectedUserId(userId)
    const client = clients.find((c) => c.userId === userId)
    const first = client?.checkIns[0]
    setSelectedCheckInId(first?.id || null)
    setFeedbackDraft(first?.coachFeedback || '')
  }

  const openCheckIn = (checkIn: WeeklyCheckInRow) => {
    setSelectedCheckInId(checkIn.id)
    setFeedbackDraft(checkIn.coachFeedback || '')
  }

  const handleSaveFeedback = async () => {
    if (!selectedCheckInId || !token) return
    setSaving(true)
    try {
      await saveCheckInFeedback(selectedCheckInId, feedbackDraft.trim(), token)
      await load()
    } catch (err) {
      console.error('Failed to save feedback:', err)
    } finally {
      setSaving(false)
    }
  }

  const filteredClients = clients.filter((client) => {
    const info = clientInfo[client.userId]
    if (!searchTerm) return true
    const haystack = `${info?.fullName || ''} ${info?.email || ''}`.toLowerCase()
    return haystack.includes(searchTerm.toLowerCase())
  })

  const totalPending = clients.reduce((sum, c) => sum + c.pendingCount, 0)

  return (
    <PanelShell
      eyebrow="Admin"
      title="Weekly Check-Ins"
      subtitle={`${totalPending} awaiting feedback · ${clients.length} client${clients.length === 1 ? '' : 's'}`}
      actions={
        <>
          <GradientButton onClick={() => load()} variant="outline" size="sm" icon={RefreshCw}>
            Refresh
          </GradientButton>
          <GradientButton onClick={() => navigate('/admin/dashboard')} variant="outline" size="sm" icon={ArrowLeft}>
            Admin home
          </GradientButton>
        </>
      }
    >
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gold" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[70vh] min-h-[500px]">
          <GlassCard hover={false} padded={false} className="lg:col-span-1 overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-9 py-2 text-sm"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredClients.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500">No check-ins yet.</div>
              ) : (
                filteredClients.map((client) => {
                  const info = clientInfo[client.userId]
                  const isSelected = selectedUserId === client.userId
                  return (
                    <button
                      key={client.userId}
                      onClick={() => openClient(client.userId)}
                      className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-colors ${
                        isSelected ? 'bg-brand-soft' : 'hover:bg-cream'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-ink truncate">
                          {info?.fullName || 'Unknown Client'}
                        </span>
                        {client.pendingCount > 0 && (
                          <span className="flex-shrink-0 text-xs font-semibold bg-red-500 text-white rounded-full px-2 py-0.5">
                            {client.pendingCount}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Week of {formatDate(client.latest.weekStartDate)}
                      </p>
                    </button>
                  )
                })
              )}
            </div>
          </GlassCard>

          <GlassCard hover={false} padded={false} className="lg:col-span-2 overflow-hidden flex flex-col">
            {!selectedClient ? (
              <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
                Select a client to review their check-ins.
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h3 className="font-display text-lg font-semibold text-ink">
                    {clientInfo[selectedClient.userId]?.fullName || 'Unknown Client'}
                  </h3>
                  <p className="text-xs text-gray-500">{clientInfo[selectedClient.userId]?.email}</p>
                </div>

                <div className="flex gap-2 px-6 py-3 border-b border-gray-100 overflow-x-auto">
                  {selectedClient.checkIns.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => openCheckIn(c)}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        selectedCheckInId === c.id
                          ? 'bg-ink text-white border-ink'
                          : 'border-gray-300 text-gray-600 hover:border-brand'
                      }`}
                    >
                      {formatDate(c.weekStartDate)}
                      {c.status === 'submitted' && <span className="ml-1.5 text-red-300">●</span>}
                    </button>
                  ))}
                </div>

                {selectedCheckIn && (
                  <div className="px-6 py-5 space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                          Workouts
                        </p>
                        <p className="text-sm text-ink mt-1">
                          {selectedCheckIn.trainingCompleted ?? '—'} / {selectedCheckIn.trainingGoal ?? '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                          Overall
                        </p>
                        <p className="text-sm text-ink mt-1">{selectedCheckIn.goalStatus || '—'}</p>
                      </div>
                    </div>

                    {selectedCheckIn.measurements && (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                          Measurements
                        </p>
                        <div className="flex gap-4 text-sm text-ink">
                          {Object.entries(selectedCheckIn.measurements).map(([k, v]) => (
                            <span key={k} className="capitalize">
                              {k}: {v}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedCheckIn.reflection && (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                          Reflection
                        </p>
                        <p className="text-sm text-textPrimary whitespace-pre-wrap">
                          {selectedCheckIn.reflection}
                        </p>
                      </div>
                    )}

                    {selectedCheckIn.photoPaths.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                          Progress photos
                        </p>
                        <div className="flex flex-wrap gap-3">
                          {selectedCheckIn.photoPaths.map((_, idx) => (
                            <AuthedImage
                              key={`${selectedCheckIn.id}-${idx}`}
                              checkInId={selectedCheckIn.id}
                              index={idx}
                              alt="Client progress"
                              className="w-20 h-20 rounded-xl object-cover border border-gray-100"
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="pt-2 border-t border-gray-100">
                      <label className="form-label">Your feedback</label>
                      <textarea
                        value={feedbackDraft}
                        onChange={(e) => setFeedbackDraft(e.target.value)}
                        rows={4}
                        className="form-input resize-none"
                        placeholder="Write feedback for this week's check-in..."
                      />
                      <GradientButton
                        onClick={handleSaveFeedback}
                        disabled={saving}
                        icon={Save}
                        size="sm"
                        className="mt-3"
                      >
                        {saving ? 'Saving...' : 'Save feedback'}
                      </GradientButton>
                    </div>
                  </div>
                )}
              </div>
            )}
          </GlassCard>
        </div>
      )}
    </PanelShell>
  )
}

export default AdminProgressCheckIns
